// src/store/slices/appointmentSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  // Admin Thunks
  getAppointmentsThunk,
  adminCreateAppointmentThunk,
  getCustomerAppointmentsThunk,
  getAppointmentDetailsThunk,
  getTodayAppointmentsThunk,
  getUpcomingAppointmentsThunk,
  searchAppointmentsThunk,
  getPendingApprovalsThunk,
  approveAppointmentThunk,
  rejectAppointmentThunk,
  bulkApproveAppointmentsThunk,

  // Appointment Modifications (OLD)
  assignAdminThunk,
  rescheduleAppointmentThunk,
  cancelAppointmentThunk,
  updateNotesThunk,

  // 🆕 NEW: Admin Assignment & Availability
  getAvailableAdminsThunk,
  reassignAdminThunk,
  unassignAdminThunk,
  autoAssignAdminThunk,
  getAdminScheduleThunk,
  bulkReassignAppointmentsThunk,

  // 🆕 NEW: Appointment Field Updates
  updateAppointmentTypeThunk,
  updateAppointmentDurationThunk,
  updateAppointmentIntentThunk,
  updateAppointmentPriorityThunk,
  addAppointmentTagsThunk,
  removeAppointmentTagsThunk,
  updateAppointmentMetadataThunk,
  updateReminderPreferencesThunk,
  bulkUpdateAppointmentsThunk,

  // Visit Tracking Thunks
  checkInCustomerThunk,
  startAppointmentThunk,
  trackProductViewedThunk,
  trackProductTriedOnThunk,
  addInteractionThunk,
  updateVisitNotesThunk,
  completeVisitThunk,
  markNoShowThunk,

  // Analytics Thunks
  getAppointmentAnalyticsThunk,
  getBranchMetricsThunk,
  getStatisticsThunk,
  getTimelineThunk,
  exportAppointmentsThunk,
  getAvailableSlotsThunk,
  getBookingInfoThunk,
  getAvailableSlotsForWeekThunk,

  // Enhanced Statistics Thunks
  getDashboardStatisticsThunk,
  getAppointmentBookingDensityThunk,
  getStatisticsByStatusThunk,
} from "../thunks/appointmentThunks";

const initialState = {
  // Lists
  appointments: [],
  todayAppointments: [],
  upcomingAppointments: [],
  pendingApprovals: [],
  searchResults: [],
  customerAppointmentHistory: [],

  // Single appointment
  selectedAppointment: null,
  selectedCustomerDetails: null,

  // Slots management
  weekSlots: {}, // { "2025-01-15": [...slots], "2025-01-16": [...slots] }
  availableSlots: [],

  // 🆕 NEW: Admin Assignment
  availableAdmins: [], // List of available admins for a slot
  unavailableAdmins: [], // List of unavailable admins with reasons
  adminSchedule: null, // Admin's schedule for date range

  // Analytics (existing)
  analytics: null,
  branchMetrics: null,
  statistics: null,
  timeline: null,

  // Enhanced Statistics
  dashboardStats: null, // Comprehensive dashboard statistics
  bookingDensity: {}, // { "2025-10-01": 5, "2025-10-02": 8 }
  densityStatistics: null, // Peak days, averages, etc.
  statusStats: [], // Statistics grouped by status

  // Loading states
  loading: false,
  loadingDetails: false,
  loadingList: false,
  loadingSlots: false,
  loadingAnalytics: false,
  submitting: false,

  // 🆕 NEW: Additional loading states
  loadingStats: false, // Dashboard stats loading
  loadingDensity: false, // Booking density loading
  loadingStatusStats: false, // Status stats loading
  loadingAvailableAdmins: false, // Available admins loading
  loadingAdminSchedule: false, // Admin schedule loading

  // Error states
  error: null,
  slotError: null,

  // 🆕 NEW: Additional error states
  statsError: null, // Dashboard stats error
  densityError: null, // Booking density error
  statusStatsError: null, // Status stats error
  availableAdminsError: null, // Available admins error

  // Filters
  filters: {
    branchCode: "",
    status: "",
    type: "",
    date: "",
    startDate: "",
    endDate: "",
    searchQuery: "",
  },

  // Pagination
  pagination: {
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
  },

  // 🆕 NEW: Bulk operation results
  bulkOperationResults: null,
};

const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    clearSelectedAppointment: (state) => {
      state.selectedAppointment = null;
      state.timeline = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
      state.statsError = null;
      state.densityError = null;
      state.statusStatsError = null;
      state.availableAdminsError = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearAvailableSlots: (state) => {
      state.availableSlots = [];
    },
    clearDashboardStats: (state) => {
      state.dashboardStats = null;
    },
    clearBookingDensity: (state) => {
      state.bookingDensity = {};
      state.densityStatistics = null;
    },
    clearAvailableAdmins: (state) => {
      state.availableAdmins = [];
      state.unavailableAdmins = [];
      state.availableAdminsError = null;
    },
    clearAdminSchedule: (state) => {
      state.adminSchedule = null;
    },
    clearBulkOperationResults: (state) => {
      state.bulkOperationResults = null;
    },
  },
  extraReducers: (builder) => {
    // ========================================
    // APPOINTMENT MANAGEMENT
    // ========================================

    // ========== GET ALL APPOINTMENTS ==========
    builder
      .addCase(getAppointmentsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAppointmentsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.appointments = action.payload.appointments;
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 20,
          totalCount: action.payload.totalCount || 0,
          totalPages: action.payload.totalPages || 0,
        };
      })
      .addCase(getAppointmentsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      });

    // ========== CREATE APPOINTMENT (ADMIN BOOKING) ==========
    builder
      .addCase(adminCreateAppointmentThunk.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(adminCreateAppointmentThunk.fulfilled, (state, action) => {
        state.submitting = false;
        state.appointments.unshift(action.payload);
      })
      .addCase(adminCreateAppointmentThunk.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });

    // ========== GET CUSTOMER APPOINTMENTS ==========
    builder
      .addCase(getCustomerAppointmentsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getCustomerAppointmentsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.customerAppointmentHistory = action.payload.appointments || [];
        if (action.payload.userDetails) {
          state.selectedCustomerDetails = action.payload.userDetails;
        }
      })
      .addCase(getCustomerAppointmentsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      });

    // ========== GET APPOINTMENT DETAILS ==========
    builder
      .addCase(getAppointmentDetailsThunk.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
      })
      .addCase(getAppointmentDetailsThunk.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.selectedAppointment =
          action.payload.appointment || action.payload;
      })
      .addCase(getAppointmentDetailsThunk.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload;
      });

    // ========== GET TODAY'S APPOINTMENTS ==========
    builder
      .addCase(getTodayAppointmentsThunk.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(getTodayAppointmentsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.todayAppointments = action.payload.appointments;
      })
      .addCase(getTodayAppointmentsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      });

    // ========== GET UPCOMING APPOINTMENTS ==========
    builder
      .addCase(getUpcomingAppointmentsThunk.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(getUpcomingAppointmentsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.upcomingAppointments = action.payload.appointments;
      })
      .addCase(getUpcomingAppointmentsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      });

    // ========== SEARCH APPOINTMENTS ==========
    builder
      .addCase(searchAppointmentsThunk.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(searchAppointmentsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.searchResults = action.payload.appointments;
      })
      .addCase(searchAppointmentsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      });

    // ========================================
    // APPROVAL WORKFLOW
    // ========================================

    // ========== GET PENDING APPROVALS ==========
    builder
      .addCase(getPendingApprovalsThunk.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(getPendingApprovalsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.pendingApprovals = action.payload.appointments || action.payload;
      })
      .addCase(getPendingApprovalsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      });

    // ========== APPROVE APPOINTMENT ==========
    builder
      .addCase(approveAppointmentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveAppointmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (apt) => apt._id !== action.payload.appointment._id
        );
      })
      .addCase(approveAppointmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== REJECT APPOINTMENT ==========
    builder
      .addCase(rejectAppointmentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(rejectAppointmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (apt) => apt._id !== action.payload.appointment._id
        );
      })
      .addCase(rejectAppointmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== BULK APPROVE ==========
    builder
      .addCase(bulkApproveAppointmentsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkApproveAppointmentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkOperationResults = action.payload;
        // Remove approved appointments from pending
        const approvedIds = action.payload.success.map((s) => s.appointmentId);
        state.pendingApprovals = state.pendingApprovals.filter(
          (apt) => !approvedIds.includes(apt._id)
        );
      })
      .addCase(bulkApproveAppointmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================
    // 🆕 NEW: ADMIN ASSIGNMENT & AVAILABILITY
    // ========================================

    // ========== GET AVAILABLE ADMINS ==========
    builder
      .addCase(getAvailableAdminsThunk.pending, (state) => {
        state.loadingAvailableAdmins = true;
        state.availableAdminsError = null;
      })
      .addCase(getAvailableAdminsThunk.fulfilled, (state, action) => {
        state.loadingAvailableAdmins = false;
        state.availableAdmins = action.payload.availableAdmins || [];
        state.unavailableAdmins = action.payload.unavailableAdmins || [];
      })
      .addCase(getAvailableAdminsThunk.rejected, (state, action) => {
        state.loadingAvailableAdmins = false;
        state.availableAdminsError = action.payload;
      });

    // ========== ASSIGN ADMIN (OLD - kept for compatibility) ==========
    builder
      .addCase(assignAdminThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(assignAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== REASSIGN ADMIN ==========
    builder
      .addCase(reassignAdminThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reassignAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
        // Update in lists if present
        const updateLists = (list) => {
          return list.map((apt) =>
            apt._id === action.payload.appointment._id
              ? action.payload.appointment
              : apt
          );
        };
        state.appointments = updateLists(state.appointments);
        state.todayAppointments = updateLists(state.todayAppointments);
        state.upcomingAppointments = updateLists(state.upcomingAppointments);
      })
      .addCase(reassignAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== UNASSIGN ADMIN ==========
    builder
      .addCase(unassignAdminThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(unassignAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(unassignAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== AUTO-ASSIGN ADMIN ==========
    builder
      .addCase(autoAssignAdminThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(autoAssignAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(autoAssignAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== GET ADMIN SCHEDULE ==========
    builder
      .addCase(getAdminScheduleThunk.pending, (state) => {
        state.loadingAdminSchedule = true;
      })
      .addCase(getAdminScheduleThunk.fulfilled, (state, action) => {
        state.loadingAdminSchedule = false;
        state.adminSchedule = action.payload;
      })
      .addCase(getAdminScheduleThunk.rejected, (state, action) => {
        state.loadingAdminSchedule = false;
        state.error = action.payload;
      });

    // ========== BULK REASSIGN ==========
    builder
      .addCase(bulkReassignAppointmentsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkReassignAppointmentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkOperationResults = action.payload;
      })
      .addCase(bulkReassignAppointmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================
    // 🆕 NEW: APPOINTMENT FIELD UPDATES
    // ========================================

    // ========== UPDATE APPOINTMENT TYPE ==========
    builder
      .addCase(updateAppointmentTypeThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppointmentTypeThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateAppointmentTypeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== UPDATE APPOINTMENT DURATION ==========
    builder
      .addCase(updateAppointmentDurationThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppointmentDurationThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateAppointmentDurationThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== UPDATE APPOINTMENT INTENT ==========
    builder
      .addCase(updateAppointmentIntentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppointmentIntentThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateAppointmentIntentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== UPDATE APPOINTMENT PRIORITY ==========
    builder
      .addCase(updateAppointmentPriorityThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppointmentPriorityThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateAppointmentPriorityThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== ADD APPOINTMENT TAGS ==========
    builder
      .addCase(addAppointmentTagsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(addAppointmentTagsThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(addAppointmentTagsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== REMOVE APPOINTMENT TAGS ==========
    builder
      .addCase(removeAppointmentTagsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeAppointmentTagsThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(removeAppointmentTagsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== UPDATE APPOINTMENT METADATA ==========
    builder
      .addCase(updateAppointmentMetadataThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppointmentMetadataThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateAppointmentMetadataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== UPDATE REMINDER PREFERENCES ==========
    builder
      .addCase(updateReminderPreferencesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateReminderPreferencesThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateReminderPreferencesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== BULK UPDATE APPOINTMENTS ==========
    builder
      .addCase(bulkUpdateAppointmentsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkUpdateAppointmentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkOperationResults = action.payload;
      })
      .addCase(bulkUpdateAppointmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================
    // APPOINTMENT MODIFICATIONS (EXISTING)
    // ========================================

    // ========== UPDATE NOTES ==========
    builder
      .addCase(updateNotesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateNotesThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateNotesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== RESCHEDULE APPOINTMENT ==========
    builder
      .addCase(rescheduleAppointmentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(rescheduleAppointmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(rescheduleAppointmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== CANCEL APPOINTMENT ==========
    builder
      .addCase(cancelAppointmentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelAppointmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(cancelAppointmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================
    // VISIT TRACKING
    // ========================================

    // ========== CHECK-IN CUSTOMER ==========
    builder
      .addCase(checkInCustomerThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkInCustomerThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(checkInCustomerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== START APPOINTMENT ==========
    builder
      .addCase(startAppointmentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(startAppointmentThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(startAppointmentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== TRACK PRODUCT VIEWED ==========
    builder
      .addCase(trackProductViewedThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(trackProductViewedThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(trackProductViewedThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== TRACK PRODUCT TRIED ON ==========
    builder
      .addCase(trackProductTriedOnThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(trackProductTriedOnThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(trackProductTriedOnThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== ADD INTERACTION ==========
    builder
      .addCase(addInteractionThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(addInteractionThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addInteractionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== UPDATE VISIT NOTES ==========
    builder
      .addCase(updateVisitNotesThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateVisitNotesThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(updateVisitNotesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== COMPLETE VISIT ==========
    builder
      .addCase(completeVisitThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(completeVisitThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(completeVisitThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== MARK NO-SHOW ==========
    builder
      .addCase(markNoShowThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(markNoShowThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedAppointment) {
          state.selectedAppointment = action.payload.appointment;
        }
      })
      .addCase(markNoShowThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================
    // ANALYTICS & REPORTING
    // ========================================

    // ========== GET ANALYTICS ==========
    builder
      .addCase(getAppointmentAnalyticsThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getAppointmentAnalyticsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics = action.payload.analytics || action.payload;
      })
      .addCase(getAppointmentAnalyticsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload;
      });

    // ========== GET BRANCH METRICS ==========
    builder
      .addCase(getBranchMetricsThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getBranchMetricsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.branchMetrics = action.payload.metrics || action.payload;
      })
      .addCase(getBranchMetricsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload;
      });

    // ========== GET STATISTICS ==========
    builder
      .addCase(getStatisticsThunk.pending, (state) => {
        state.loadingAnalytics = true;
      })
      .addCase(getStatisticsThunk.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.statistics = action.payload.statistics || action.payload;
      })
      .addCase(getStatisticsThunk.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload;
      });

    // ========================================
    // ENHANCED STATISTICS
    // ========================================

    // ========== GET DASHBOARD STATISTICS ==========
    builder
      .addCase(getDashboardStatisticsThunk.pending, (state) => {
        state.loadingStats = true;
        state.statsError = null;
      })
      .addCase(getDashboardStatisticsThunk.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.dashboardStats = action.payload;
      })
      .addCase(getDashboardStatisticsThunk.rejected, (state, action) => {
        state.loadingStats = false;
        state.statsError = action.payload;
      });

    // ========== GET BOOKING DENSITY ==========
    builder
      .addCase(getAppointmentBookingDensityThunk.pending, (state) => {
        state.loadingDensity = true;
        state.densityError = null;
      })
      .addCase(getAppointmentBookingDensityThunk.fulfilled, (state, action) => {
        state.loadingDensity = false;
        state.bookingDensity = action.payload.density || {};
        state.densityStatistics = action.payload.statistics || null;
      })
      .addCase(getAppointmentBookingDensityThunk.rejected, (state, action) => {
        state.loadingDensity = false;
        state.densityError = action.payload;
      });

    // ========== GET STATISTICS BY STATUS ==========
    builder
      .addCase(getStatisticsByStatusThunk.pending, (state) => {
        state.loadingStatusStats = true;
        state.statusStatsError = null;
      })
      .addCase(getStatisticsByStatusThunk.fulfilled, (state, action) => {
        state.loadingStatusStats = false;
        state.statusStats = action.payload.statistics || [];
      })
      .addCase(getStatisticsByStatusThunk.rejected, (state, action) => {
        state.loadingStatusStats = false;
        state.statusStatsError = action.payload;
      });

    // ========== GET TIMELINE ==========
    builder
      .addCase(getTimelineThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTimelineThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.timeline = action.payload.timeline || action.payload;
      })
      .addCase(getTimelineThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========== EXPORT APPOINTMENTS ==========
    builder
      .addCase(exportAppointmentsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportAppointmentsThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportAppointmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================
    // SLOT AVAILABILITY
    // ========================================

    // ========== GET AVAILABLE SLOTS FOR WEEK ==========
    builder
      .addCase(getAvailableSlotsForWeekThunk.pending, (state) => {
        state.loadingSlots = true;
        state.slotError = null;
      })
      .addCase(getAvailableSlotsForWeekThunk.fulfilled, (state, action) => {
        state.loadingSlots = false;
        state.weekSlots = {
          ...state.weekSlots,
          ...action.payload.slotsByDate,
        };
      })
      .addCase(getAvailableSlotsForWeekThunk.rejected, (state, action) => {
        state.loadingSlots = false;
        state.slotError = action.payload;
      });

    // ========== GET AVAILABLE SLOTS ==========
    builder
      .addCase(getAvailableSlotsThunk.pending, (state) => {
        state.loadingSlots = true;
        state.availableSlots = [];
      })
      .addCase(getAvailableSlotsThunk.fulfilled, (state, action) => {
        state.loadingSlots = false;
        state.availableSlots = action.payload.slots || action.payload;
      })
      .addCase(getAvailableSlotsThunk.rejected, (state, action) => {
        state.loadingSlots = false;
        state.error = action.payload;
      });

    // ========== GET BOOKING INFO ==========
    builder
      .addCase(getBookingInfoThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBookingInfoThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.availableSlots = action.payload.data?.availableSlots || [];
      })
      .addCase(getBookingInfoThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearSelectedAppointment,
  setFilters,
  clearFilters,
  clearError,
  clearSearchResults,
  clearAvailableSlots,
  clearDashboardStats,
  clearBookingDensity,
  clearAvailableAdmins,
  clearAdminSchedule,
  clearBulkOperationResults,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
